import * as z from 'zod';

export const PreviewPriceRequest = z.object({
  bookingLineItems: z
    .array(
      z.object({
        serviceId: z
          .string()
          .describe(
            "[Service ID](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction).\n\nRequired parameter in:\n- [Preview Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/preview-price) for all service types. All line items must have the same service ID.\n- [Calculate Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/calculate-price) when using Wix Bookings' default pricing logic. Optional in Calculate Price when using a Pricing Service Provider Plugin."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        resourceId: z
          .string()
          .describe(
            '[Resource ID](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction).\n\nRequired parameter in:\n- [Calculate Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/calculate-price) for all service types ([service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types)).\n- [Preview Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/preview-price) for appointment-based services and classes. Optional in Preview Price for courses.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        choices: z
          .array(
            z.intersection(
              z.object({
                optionId: z
                  .string()
                  .describe(
                    'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  custom: z.never().optional(),
                  duration: z.never().optional(),
                }),
                z.object({
                  duration: z.never().optional(),
                  custom: z
                    .string()
                    .describe(
                      'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                    ),
                }),
                z.object({
                  custom: z.never().optional(),
                  duration: z.object({
                    minutes: z
                      .number()
                      .int()
                      .describe(
                        'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                      )
                      .min(1)
                      .max(44639)
                      .optional(),
                    name: z
                      .string()
                      .describe(
                        'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                      )
                      .max(255)
                      .optional()
                      .nullable(),
                  }),
                }),
              ])
            )
          )
          .optional(),
        numberOfParticipants: z
          .number()
          .int()
          .describe(
            '__Deprecated.__ Use `serviceChoices.numberOfParticipants` instead.\n\nNumber of participants for the line item.'
          )
          .min(1)
          .optional()
          .nullable(),
        pricePerParticipant: z
          .number()
          .describe(
            '__Deprecated.__ Use `serviceChoices.pricePerParticipant` instead.\n\nPrice per participant for the line item.'
          )
          .optional()
          .nullable(),
        serviceChoices: z
          .array(
            z.object({
              numberOfParticipants: z
                .number()
                .int()
                .describe('Number of participants for this service variant.')
                .min(1)
                .optional()
                .nullable(),
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      optionId: z
                        .string()
                        .describe(
                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        custom: z.never().optional(),
                        duration: z.never().optional(),
                      }),
                      z.object({
                        duration: z.never().optional(),
                        custom: z
                          .string()
                          .describe(
                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                          ),
                      }),
                      z.object({
                        custom: z.never().optional(),
                        duration: z.object({
                          minutes: z
                            .number()
                            .int()
                            .describe(
                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                            )
                            .min(1)
                            .max(44639)
                            .optional(),
                          name: z
                            .string()
                            .describe(
                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                            )
                            .max(255)
                            .optional()
                            .nullable(),
                        }),
                      }),
                    ])
                  )
                )
                .max(5)
                .optional(),
              pricePerParticipant: z
                .number()
                .describe('Price per participant for this service variant.')
                .optional()
                .nullable(),
            })
          )
          .max(20)
          .optional(),
      })
    )
    .min(1)
    .max(20),
});
export const PreviewPriceResponse = z.object({
  priceInfo: z
    .intersection(
      z.object({
        bookingLineItems: z
          .array(
            z.object({
              serviceId: z
                .string()
                .describe(
                  "[Service ID](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction).\n\nRequired parameter in:\n- [Preview Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/preview-price) for all service types. All line items must have the same service ID.\n- [Calculate Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/calculate-price) when using Wix Bookings' default pricing logic. Optional in Calculate Price when using a Pricing Service Provider Plugin."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              resourceId: z
                .string()
                .describe(
                  '[Resource ID](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction).\n\nRequired parameter in:\n- [Calculate Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/calculate-price) for all service types ([service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types)).\n- [Preview Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/preview-price) for appointment-based services and classes. Optional in Preview Price for courses.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      optionId: z
                        .string()
                        .describe(
                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        custom: z.never().optional(),
                        duration: z.never().optional(),
                      }),
                      z.object({
                        duration: z.never().optional(),
                        custom: z
                          .string()
                          .describe(
                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                          ),
                      }),
                      z.object({
                        custom: z.never().optional(),
                        duration: z.object({
                          minutes: z
                            .number()
                            .int()
                            .describe(
                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                            )
                            .min(1)
                            .max(44639)
                            .optional(),
                          name: z
                            .string()
                            .describe(
                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                            )
                            .max(255)
                            .optional()
                            .nullable(),
                        }),
                      }),
                    ])
                  )
                )
                .optional(),
              numberOfParticipants: z
                .number()
                .int()
                .describe(
                  '__Deprecated.__ Use `serviceChoices.numberOfParticipants` instead.\n\nNumber of participants for the line item.'
                )
                .min(1)
                .optional()
                .nullable(),
              pricePerParticipant: z
                .number()
                .describe(
                  '__Deprecated.__ Use `serviceChoices.pricePerParticipant` instead.\n\nPrice per participant for the line item.'
                )
                .optional()
                .nullable(),
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this service variant.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                    pricePerParticipant: z
                      .number()
                      .describe(
                        'Price per participant for this service variant.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(20)
                .optional(),
            })
          )
          .max(20)
          .optional(),
        deposit: z
          .number()
          .describe(
            "Total deposit the customer must pay when booking the service.\nAvailable if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.options.deposit` is set to `true`."
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          calculatedPrice: z.never().optional(),
          priceDescription: z.never().optional(),
          priceDescriptionInfo: z.never().optional(),
        }),
        z.object({
          priceDescription: z.never().optional(),
          priceDescriptionInfo: z.never().optional(),
          calculatedPrice: z
            .number()
            .describe(
              "Calculated total price. Available if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.rateType` is `FIXED`, `VARIED`, or `NO_FEE`."
            ),
        }),
        z.object({
          calculatedPrice: z.never().optional(),
          priceDescriptionInfo: z.never().optional(),
          priceDescription: z
            .string()
            .describe(
              "Description of the total price.\nAvailable if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.rateType` is `CUSTOM`."
            ),
        }),
        z.object({
          calculatedPrice: z.never().optional(),
          priceDescription: z.never().optional(),
          priceDescriptionInfo: z
            .object({
              original: z
                .string()
                .describe(
                  "Price description in the site's main language.\nRefer to the [Site Properties API](https://dev.wix.com/docs/api-reference/business-management/site-properties/properties/introduction) for more details."
                )
                .max(50)
                .optional(),
              translated: z
                .string()
                .describe(
                  'Translated price description.\nAvailable if the booking was made in a secondary language.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              "Description of the total price.\nAvailable if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.rateType` is `CUSTOM`."
            ),
        }),
      ])
    )
    .describe(
      'Pricing information including individual line item costs and total estimated price.'
    )
    .optional(),
});
export const CalculatePriceRequest = z.object({
  booking: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedEntity: z
          .intersection(
            z.object({
              title: z
                .string()
                .describe(
                  'Session title at the time of booking. If there is no pre-existing session,\nfor example for appointment-based services, Wix Bookings sets `title` to the service name.'
                )
                .max(6000)
                .optional()
                .nullable(),
              tags: z.array(z.string()).optional(),
            }),
            z.xor([
              z.object({
                slot: z.never().optional(),
                schedule: z.never().optional(),
              }),
              z.object({
                schedule: z.never().optional(),
                slot: z
                  .object({
                    sessionId: z
                      .string()
                      .describe(
                        'Session ID. For class bookings, this is the ID of the existing session.\nFor appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.\n\n__Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).\nYou can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.'
                      )
                      .optional()
                      .nullable(),
                    serviceId: z.string().describe('Service ID.').optional(),
                    scheduleId: z.string().describe('Schedule ID.').optional(),
                    eventId: z
                      .string()
                      .describe(
                        'ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).\nAvailable for both appointment and class bookings, not available for course bookings.\nFor appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.\nFor class bookings, Wix Bookings automatically populates `eventId` upon booking creation.'
                      )
                      .min(36)
                      .max(250)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    resource: z
                      .object({
                        _id: z
                          .string()
                          .describe("ID of the booking's primary resource.")
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe("Resource's name at the time of booking.")
                          .max(40)
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe("Resource's email at the time of booking.")
                          .max(500)
                          .optional()
                          .nullable(),
                        scheduleId: z
                          .string()
                          .describe(
                            "ID of the schedule belonging to the booking's primary resource."
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.\nFor example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.'
                      )
                      .optional(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe('Location where the session takes place.')
                      .optional(),
                    resourceSelections: z
                      .array(
                        z.object({
                          resourceTypeId: z
                            .string()
                            .describe(
                              'ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          selectionMethod: z
                            .enum([
                              'SPECIFIC_RESOURCE',
                              'ANY_RESOURCE',
                              'NO_SELECTION',
                            ])
                            .optional(),
                        })
                      )
                      .max(3)
                      .optional(),
                  })
                  .describe(
                    '[Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).\n\nSpecify `slot` when creating bookings for:\n- __Appointment-based services__: Individual sessions with service providers (consultations, treatments).\nWix Bookings creates a new session when the booking is confirmed.\n- __Class services__: Group sessions at specific times (fitness classes, workshops).\nWix Bookings links the booking to an existing scheduled session.\n\nFor course services, specify `schedule` instead of `slot`.'
                  ),
              }),
              z.object({
                slot: z.never().optional(),
                schedule: z
                  .object({
                    scheduleId: z
                      .string()
                      .describe(
                        '[Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).'
                      )
                      .optional(),
                    serviceId: z
                      .string()
                      .describe('Booked service ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    location: z
                      .object({
                        _id: z
                          .string()
                          .describe(
                            'Business location ID. Available only for locations that are business locations,\nmeaning the `location_type` is `"OWNER_BUSINESS"`.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Location name.')
                          .optional()
                          .nullable(),
                        formattedAddress: z
                          .string()
                          .describe('The full address of this location.')
                          .optional()
                          .nullable(),
                        formattedAddressTranslated: z
                          .string()
                          .describe(
                            'The full translated address of this location.'
                          )
                          .max(512)
                          .optional()
                          .nullable(),
                        locationType: z
                          .enum([
                            'UNDEFINED',
                            'OWNER_BUSINESS',
                            'OWNER_CUSTOM',
                            'CUSTOM',
                          ])
                          .describe('Location type.')
                          .optional(),
                      })
                      .describe(
                        "[Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place."
                      )
                      .optional(),
                    timezone: z
                      .string()
                      .describe(
                        'Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.'
                      )
                      .optional()
                      .nullable(),
                    firstSessionStart: z
                      .string()
                      .describe(
                        'Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                    lastSessionEnd: z
                      .string()
                      .describe(
                        'End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    '[Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).\n\nSpecify `schedule` when creating bookings for:\n- __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).\nWix Bookings enrolls participants in all sessions defined by the course schedule.'
                  ),
              }),
            ])
          )
          .describe(
            'An object describing the bookable entity - either a specific time slot or a recurring schedule.\n\nThe structure depends on the type of service being booked:\n\n__For appointment services__: Use `slot` to book a specific time slot with a\nservice provider. Appointments are typically one-time sessions at a specific date and time.\n\n__For class services__: Use `slot` to book a specific class session. Classes\nare individual sessions that can have multiple participants.\n\n__For course services__: Use `schedule` to book an entire course consisting of\nmultiple sessions over time. Courses are recurring, multi-session offerings.\n\nChoose the appropriate field based on your service type and booking requirements.'
          )
          .optional(),
        contactDetails: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            firstName: z
              .string()
              .describe(
                "Contact's first name. When populated from a standard booking form, this\nproperty corresponds to the `name` field."
              )
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Contact's last name.")
              .optional()
              .nullable(),
            email: z
              .string()
              .describe(
                "Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)\nwith this email exist, a new contact is created.\nUsed to validate coupon usage limitations per contact. If not specified,\nthe coupon usage limitation will not be enforced. (Coupon usage limitation\nvalidation is not supported yet)."
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe("Contact's phone number.")
              .optional()
              .nullable(),
            fullAddress: z
              .intersection(
                z.object({
                  country: z
                    .string()
                    .describe('Country code.')
                    .optional()
                    .nullable(),
                  subdivision: z
                    .string()
                    .describe(
                      'Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                    )
                    .optional()
                    .nullable(),
                  city: z.string().describe('City name.').optional().nullable(),
                  postalCode: z
                    .string()
                    .describe('Zip/postal code.')
                    .optional()
                    .nullable(),
                  addressLine2: z
                    .string()
                    .describe(
                      'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                    )
                    .optional()
                    .nullable(),
                  formattedAddress: z
                    .string()
                    .describe(
                      'A string containing the full address of this location.'
                    )
                    .optional()
                    .nullable(),
                  hint: z
                    .string()
                    .describe('Free text to help find the address.')
                    .optional()
                    .nullable(),
                  geocode: z
                    .object({
                      latitude: z
                        .number()
                        .describe('Address latitude.')
                        .optional()
                        .nullable(),
                      longitude: z
                        .number()
                        .describe('Address longitude.')
                        .optional()
                        .nullable(),
                    })
                    .describe('Coordinates of the physical address.')
                    .optional(),
                  countryFullname: z
                    .string()
                    .describe('Country full name.')
                    .optional()
                    .nullable(),
                  subdivisions: z
                    .array(
                      z.object({
                        code: z
                          .string()
                          .describe(
                            'Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Subdivision full name.')
                          .optional(),
                      })
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z.never().optional(),
                  }),
                  z.object({
                    addressLine: z.never().optional(),
                    streetAddress: z
                      .object({
                        number: z
                          .string()
                          .describe('Street number.')
                          .optional(),
                        name: z.string().describe('Street name.').optional(),
                        apt: z
                          .string()
                          .describe('Apartment number.')
                          .optional(),
                      })
                      .describe('Street name, number and apartment number.'),
                  }),
                  z.object({
                    streetAddress: z.never().optional(),
                    addressLine: z
                      .string()
                      .describe(
                        'Main address line, usually street and number, as free text.'
                      ),
                  }),
                ])
              )
              .describe("Contact's full address.")
              .optional(),
            timeZone: z
              .string()
              .describe("Contact's time zone.")
              .optional()
              .nullable(),
            countryCode: z
              .string()
              .describe(
                "Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)\nformat."
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)\nmaking the booking.'
          )
          .optional(),
        additionalFields: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('ID of the form field as defined in the form.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              value: z
                .string()
                .describe('Value that was submitted for this field.')
                .optional()
                .nullable(),
              label: z
                .string()
                .describe("Form field's label at the time of submission.")
                .optional()
                .nullable(),
              valueType: z
                .enum(['SHORT_TEXT', 'LONG_TEXT', 'CHECK_BOX'])
                .optional(),
            })
          )
          .optional(),
        status: z
          .enum([
            'CREATED',
            'CONFIRMED',
            'CANCELED',
            'PENDING',
            'DECLINED',
            'WAITING_LIST',
          ])
          .optional(),
        paymentStatus: z
          .enum([
            'UNDEFINED',
            'NOT_PAID',
            'PAID',
            'PARTIALLY_PAID',
            'REFUNDED',
            'EXEMPT',
          ])
          .optional(),
        selectedPaymentOption: z
          .enum([
            'UNDEFINED',
            'OFFLINE',
            'ONLINE',
            'MEMBERSHIP',
            'MEMBERSHIP_OFFLINE',
          ])
          .optional(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        externalUserId: z
          .string()
          .describe('External user ID that you can provide.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number to be used when updating, rescheduling, or cancelling the booking.\nIncrements by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.'
          )
          .optional()
          .nullable(),
        createdBy: z
          .intersection(
            z.object({
              contactId: z
                .string()
                .describe(
                  "ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has not logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z.never().optional(),
                memberId: z
                  .string()
                  .describe(
                    'ID of a site visitor that has logged in to the site.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                appId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user (site owner, contributor, etc.).')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                appId: z
                  .string()
                  .describe('ID of an app.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'ID of the creator of the booking.\nIf `appId` and another ID are present, the other ID takes precedence.'
          )
          .optional(),
        startDate: z
          .date()
          .describe(
            'The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.'
          )
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.\nFor a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for this object.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        doubleBooked: z
          .boolean()
          .describe(
            'Whether this booking overlaps with another confirmed booking. Returned\nonly if set to `true`.'
          )
          .optional()
          .nullable(),
        formSubmissionId: z
          .string()
          .describe(
            'ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)\nassociated with this booking.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            "ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)\nassociated with this booking. The value depends on how the\nbooking was created:\n- For bookings created with Create Booking or Bulk Create Booking, `formId`\nis identical to ID of the booking form that's associated with the relevant\nservice.\n- For bookings created via Create Multi Service Booking, `formId` is set to\n`00000000-0000-0000-0000-000000000000` (the default booking form ID)."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookedAddOns: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('The ID of the add-on.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              groupId: z
                .string()
                .describe('The ID of the add-on group.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The add-on duration in minutes at the time of booking.\nPopulated for duration-based add-ons.'
                )
                .min(1)
                .max(1440)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'The quantity of booked add-ons.\nFor duration-based add-ons, `quantity` is not applicable.\nIf `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.\nIf any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".'
                )
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Add-on `name` at the time of booking.')
                .max(100)
                .optional()
                .nullable(),
              nameTranslated: z
                .string()
                .describe(
                  'Add-on name translated to the language the customer used during booking.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
          )
          .max(21)
          .optional(),
        appId: z
          .string()
          .describe(
            'ID of the app that\'s associated with the booking.\nInherited from the `appId` of the service associated with the booking.\nBookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.\nFor bookings from Wix apps, the following values apply:\n- Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`\n- Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`\n- Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`\n[Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        createdByAppId: z
          .string()
          .describe(
            "ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.\nThis read-only field is automatically populated during booking creation by checking these sources in order:\n1. The caller's App ID from the request identity context (external app or server identity).\n2. The booking's `appId` field (inherited from the service's `appId`).\n3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.\n<!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->"
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        depositSelected: z
          .boolean()
          .describe(
            'Whether the customer chooses to pay only the deposit amount upfront.\n\n- `true`: The customer pays only the deposit amount upfront.\n- `false`: The customer pays the full price upfront.\n\nUsed only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.\n\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.\nWhen the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z.never().optional(),
        }),
        z.object({
          participantsChoices: z.never().optional(),
          totalParticipants: z
            .number()
            .int()
            .describe(
              "Total number of participants.\nWhen creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)."
            )
            .min(1),
        }),
        z.object({
          totalParticipants: z.never().optional(),
          participantsChoices: z
            .object({
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                  })
                )
                .min(1)
                .max(20)
                .optional(),
            })
            .describe(
              'Information about the booked service choices and participant count for each choice.\nWhen creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).\n\nFor example, use this for a spa package booking that includes different service levels:\n- 2 participants chose "Standard Package".\n- 1 participant chose "VIP Package".'
            ),
        }),
      ])
    )
    .describe(
      'Existing [booking](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings/bookings-writer-v2/introduction) to calculate the base price for.\nMust include `bookedEntity` details and participant information.'
    ),
});
export const CalculatePriceResponse = z.object({
  priceInfo: z
    .intersection(
      z.object({
        bookingLineItems: z
          .array(
            z.object({
              serviceId: z
                .string()
                .describe(
                  "[Service ID](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction).\n\nRequired parameter in:\n- [Preview Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/preview-price) for all service types. All line items must have the same service ID.\n- [Calculate Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/calculate-price) when using Wix Bookings' default pricing logic. Optional in Calculate Price when using a Pricing Service Provider Plugin."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              resourceId: z
                .string()
                .describe(
                  '[Resource ID](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction).\n\nRequired parameter in:\n- [Calculate Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/calculate-price) for all service types ([service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types)).\n- [Preview Price](https://dev.wix.com/docs/api-reference/business-solutions/bookings/pricing/pricing-api/preview-price) for appointment-based services and classes. Optional in Preview Price for courses.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      optionId: z
                        .string()
                        .describe(
                          'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        custom: z.never().optional(),
                        duration: z.never().optional(),
                      }),
                      z.object({
                        duration: z.never().optional(),
                        custom: z
                          .string()
                          .describe(
                            'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                          ),
                      }),
                      z.object({
                        custom: z.never().optional(),
                        duration: z.object({
                          minutes: z
                            .number()
                            .int()
                            .describe(
                              'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                            )
                            .min(1)
                            .max(44639)
                            .optional(),
                          name: z
                            .string()
                            .describe(
                              'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                            )
                            .max(255)
                            .optional()
                            .nullable(),
                        }),
                      }),
                    ])
                  )
                )
                .optional(),
              numberOfParticipants: z
                .number()
                .int()
                .describe(
                  '__Deprecated.__ Use `serviceChoices.numberOfParticipants` instead.\n\nNumber of participants for the line item.'
                )
                .min(1)
                .optional()
                .nullable(),
              pricePerParticipant: z
                .number()
                .describe(
                  '__Deprecated.__ Use `serviceChoices.pricePerParticipant` instead.\n\nPrice per participant for the line item.'
                )
                .optional()
                .nullable(),
              serviceChoices: z
                .array(
                  z.object({
                    numberOfParticipants: z
                      .number()
                      .int()
                      .describe(
                        'Number of participants for this service variant.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            optionId: z
                              .string()
                              .describe(
                                'ID of the corresponding option for the choice. For example, the choice `child`\ncould correspond to the option `ageGroup`. In this case, `optionId` is the ID\nfor the `ageGroup` option.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              custom: z.never().optional(),
                              duration: z.never().optional(),
                            }),
                            z.object({
                              duration: z.never().optional(),
                              custom: z
                                .string()
                                .describe(
                                  'Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.\nChoices are specific values for an option the customer can choose to book. For example,\nthe option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.\nEach choice may have a different price.'
                                ),
                            }),
                            z.object({
                              custom: z.never().optional(),
                              duration: z.object({
                                minutes: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration of the service in minutes.\nMin: 1 minute, Max: 30 days, 23 hours, and 59 minutes'
                                  )
                                  .min(1)
                                  .max(44639)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Name of the duration option.\nDefaults to the formatted duration e.g. "1 hour, 30 minutes".'
                                  )
                                  .max(255)
                                  .optional()
                                  .nullable(),
                              }),
                            }),
                          ])
                        )
                      )
                      .max(5)
                      .optional(),
                    pricePerParticipant: z
                      .number()
                      .describe(
                        'Price per participant for this service variant.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(20)
                .optional(),
            })
          )
          .max(20)
          .optional(),
        deposit: z
          .number()
          .describe(
            "Total deposit the customer must pay when booking the service.\nAvailable if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.options.deposit` is set to `true`."
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          calculatedPrice: z.never().optional(),
          priceDescription: z.never().optional(),
          priceDescriptionInfo: z.never().optional(),
        }),
        z.object({
          priceDescription: z.never().optional(),
          priceDescriptionInfo: z.never().optional(),
          calculatedPrice: z
            .number()
            .describe(
              "Calculated total price. Available if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.rateType` is `FIXED`, `VARIED`, or `NO_FEE`."
            ),
        }),
        z.object({
          calculatedPrice: z.never().optional(),
          priceDescriptionInfo: z.never().optional(),
          priceDescription: z
            .string()
            .describe(
              "Description of the total price.\nAvailable if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.rateType` is `CUSTOM`."
            ),
        }),
        z.object({
          calculatedPrice: z.never().optional(),
          priceDescription: z.never().optional(),
          priceDescriptionInfo: z
            .object({
              original: z
                .string()
                .describe(
                  "Price description in the site's main language.\nRefer to the [Site Properties API](https://dev.wix.com/docs/api-reference/business-management/site-properties/properties/introduction) for more details."
                )
                .max(50)
                .optional(),
              translated: z
                .string()
                .describe(
                  'Translated price description.\nAvailable if the booking was made in a secondary language.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              "Description of the total price.\nAvailable if the [service's](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) `payment.rateType` is `CUSTOM`."
            ),
        }),
      ])
    )
    .describe(
      "Information about each line item's base price and the total base price."
    )
    .optional(),
});
